import QRCode from 'react-qr-code';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faSnowflake, faMedal } from '@fortawesome/free-solid-svg-icons';
import './LoyaltyCardDisplay.scss';

const TIER_META = {
    BRONZE: {
        label: 'Bronze',
        icon: faMedal,
        gradient: 'linear-gradient(135deg, #92400e 0%, #b45309 50%, #d97706 100%)',
        badgeBg: 'rgba(0,0,0,0.25)',
        textColor: '#fef3c7',
    },
    SILVER: {
        label: 'Silver',
        icon: faSnowflake,
        gradient: 'linear-gradient(135deg, #374151 0%, #6b7280 50%, #9ca3af 100%)',
        badgeBg: 'rgba(0,0,0,0.25)',
        textColor: '#f3f4f6',
    },
    GOLD: {
        label: 'Gold',
        icon: faStar,
        gradient: 'linear-gradient(135deg, #78350f 0%, #b45309 30%, #f59e0b 70%, #fcd34d 100%)',
        badgeBg: 'rgba(0,0,0,0.2)',
        textColor: '#fffbeb',
    },
};

const formatCardNumber = (num) =>
    num ? num.replace(/-/g, ' · ') : '';

const formatPoints = (pts) =>
    new Intl.NumberFormat('fr-MA').format(pts ?? 0);

export default function LoyaltyCardDisplay({ card, onEarn, onRedeem, onToggleStatus }) {
    if (!card) return null;
    const tier = TIER_META[card.tier] ?? TIER_META.BRONZE;
    const isActive = card.status === 'ACTIVE';

    return (
        <div className="loyalty-card-wrapper">
            {/* Visual Card */}
            <div className="loyalty-card" style={{ background: tier.gradient }}>
                {!isActive && (
                    <div className="loyalty-card__suspended-banner">
                        {card.status === 'SUSPENDED' ? 'Suspendue' : 'Expirée'}
                    </div>
                )}

                <div className="loyalty-card__top">
                    <span className="loyalty-card__pharmacy">SAYDALANI</span>
                    <span className="loyalty-card__tier-badge" style={{ background: tier.badgeBg }}>
                        <FontAwesomeIcon icon={tier.icon} className="me-1" />
                        {tier.label}
                    </span>
                </div>

                <div className="loyalty-card__number" style={{ color: tier.textColor }}>
                    {formatCardNumber(card.cardNumber)}
                </div>

                <div className="loyalty-card__bottom">
                    <div className="loyalty-card__patient" style={{ color: tier.textColor }}>
                        <span className="loyalty-card__patient-label">Titulaire</span>
                        <span className="loyalty-card__patient-name">
                            {card.patient?.firstName} {card.patient?.lastName}
                        </span>
                    </div>
                    <div className="loyalty-card__qr">
                        <QRCode
                            value={card.qrCodeData || card.cardNumber}
                            size={64}
                            bgColor="transparent"
                            fgColor={tier.textColor}
                            level="M"
                        />
                    </div>
                </div>
            </div>

            {/* Points summary */}
            <div className="loyalty-points-bar">
                <div className="loyalty-points-bar__item">
                    <span className="loyalty-points-bar__value">{formatPoints(card.currentPoints)}</span>
                    <span className="loyalty-points-bar__label">Points</span>
                </div>
                <div className="loyalty-points-bar__divider" />
                <div className="loyalty-points-bar__item">
                    <span className="loyalty-points-bar__value">
                        {(card.currentPoints * 0.10).toFixed(2)} dhs
                    </span>
                    <span className="loyalty-points-bar__label">Valeur</span>
                </div>
                <div className="loyalty-points-bar__divider" />
                <div className="loyalty-points-bar__item">
                    <span className="loyalty-points-bar__value">{tier.label}</span>
                    <span className="loyalty-points-bar__label">Niveau</span>
                </div>
                <div className="loyalty-points-bar__divider" />
                <div className="loyalty-points-bar__item">
                    <span className={`loyalty-points-bar__value loyalty-points-bar__value--${card.status?.toLowerCase()}`}>
                        {card.status === 'ACTIVE' ? 'Active' : card.status === 'SUSPENDED' ? 'Suspendue' : 'Expirée'}
                    </span>
                    <span className="loyalty-points-bar__label">Statut</span>
                </div>
            </div>

            {/* Tier progress */}
            <TierProgress points={card.currentPoints} />

            {/* Actions */}
            <div className="loyalty-actions">
                {isActive && onEarn && (
                    <button className="loyalty-btn loyalty-btn--earn" onClick={onEarn}>
                        + Créditer des points
                    </button>
                )}
                {isActive && card.currentPoints > 0 && onRedeem && (
                    <button className="loyalty-btn loyalty-btn--redeem" onClick={onRedeem}>
                        Utiliser des points
                    </button>
                )}
                {onToggleStatus && (
                    <button
                        className={`loyalty-btn loyalty-btn--status ${isActive ? 'loyalty-btn--suspend' : 'loyalty-btn--activate'}`}
                        onClick={onToggleStatus}
                    >
                        {isActive ? 'Suspendre' : 'Réactiver'}
                    </button>
                )}
            </div>
        </div>
    );
}

function TierProgress({ points }) {
    const tiers = [
        { label: 'Bronze', min: 0, max: 500 },
        { label: 'Silver', min: 500, max: 2000 },
        { label: 'Gold', min: 2000, max: null },
    ];

    const currentTierIdx = points >= 2000 ? 2 : points >= 500 ? 1 : 0;
    const current = tiers[currentTierIdx];
    const next = tiers[currentTierIdx + 1];

    const pct = next
        ? Math.min(100, ((points - current.min) / (current.max - current.min)) * 100)
        : 100;

    return (
        <div className="loyalty-tier-progress">
            <div className="loyalty-tier-progress__header">
                <span className="loyalty-tier-progress__current">{current.label}</span>
                {next && (
                    <span className="loyalty-tier-progress__next">
                        {current.max - points} pts pour atteindre {next.label}
                    </span>
                )}
                {!next && <span className="loyalty-tier-progress__next">Niveau maximum atteint ✓</span>}
            </div>
            <div className="loyalty-tier-progress__bar">
                <div className="loyalty-tier-progress__fill" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}
